'use client';

import { faChevronDown, faChevronUp, faPlus, faRedo, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import { Button, Tooltip } from '@ui';
import { StyledCheckbox, StyledInput } from '@ui/Form';
import { AnimatePresence, motion } from 'framer-motion';
import { useApi } from 'hooks/useApi';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { v4 as uuid } from 'uuid';
import useModalManager from '~/hooks/useModalManager';
import { Form } from '../Form/base/Form';
import {
    CheckboxOptions,
    DateFieldOptions,
    FieldTypeInfo,
    IBANFieldOptions,
    LegalOptions,
    NumberFieldOptions,
    PaymentOptionsField,
    SectionOptions,
    SelectOptions,
    TableOptions,
    TextFieldOptions,
    TextOptions,
    TrainingplanOptions,
    UserInvoicesOptions
} from './FieldTypeOptions';
import { FormAssistentProps, FormField, FormSubmitPayload, FormValues } from './FormAssistent.types';
import FormFields from './FormFields';
import { FormKIAssistant, GeneratedForm } from './FormKIAssistant';
import { cloneField, ensureUniqueFieldNames } from './helpers/fieldIdentity';
import QuestionBlock from './QuestionBlock';

// ── Constants ─────────────────────────────────────────────────────────────────

const RESTRICTED_FIELD_NAMES = ['email', 'createUser'] as const;
const TRAININGPLAN_PREFIX = 'trainingplan_';
const INVOICE_PREFIX = 'invoice_';

const DEFAULT_FIELDS: FormField[] = [
    { type: 'text', placeholder: '', name: 'firstname', rules: ['required'], label: 'Vorname (Trainierende*r)' },
    { type: 'text', placeholder: '', name: 'lastname', rules: ['required'], label: 'Nachname (Trainierende*r)' }
];

const normalizeTextOptions = (options: FormField['options']) =>
    (Array.isArray(options) ? options : [])
        .map((option: unknown) => {
            // AI may return { value, label } or { label } instead of plain strings, or a number
            if (typeof option === 'string') return option.trim();
            if (typeof option === 'number') return String(option);
            if (option && typeof option === 'object') {
                const o = option as Record<string, unknown>;
                return String(o.label ?? o.value ?? '').trim();
            }
            return '';
        })
        .filter(Boolean);

const normalizePaymentOptions = (options: FormField['options']) =>
    (Array.isArray(options) ? options : [])
        .filter(
            (option): option is { option: string; price: number } =>
                typeof option === 'object' &&
                option !== null &&
                'option' in option &&
                typeof option.option === 'string' &&
                option.option.trim().length > 0
        )
        .map((option) => ({ ...option, option: option.option.trim() }));

const sanitizeField = (field: FormField): FormField => {
    if (field.type === 'section') {
        return {
            ...field,
            children: (field.children ?? []).map((child) => ({
                ...child,
                linkedValues: (child.linkedValues ?? []).filter(Boolean),
                fields: sanitizeFields(child.fields)
            }))
        };
    }

    if (field.type === 'select') {
        return { ...field, options: normalizeTextOptions(field.options) };
    }

    if (field.type === 'checkbox' && Array.isArray(field.options) && field.options.length > 0) {
        return { ...field, options: normalizeTextOptions(field.options) };
    }

    if (field.type === 'payment_options') {
        return { ...field, options: normalizePaymentOptions(field.options) };
    }

    return field;
};

const sanitizeFields = (fields: FormField[] | undefined) => (fields ?? []).map(sanitizeField);

const collectMissingLabelFieldNames = (fields: FormField[] | undefined): string[] => {
    const names: string[] = [];

    for (const field of fields ?? []) {
        if (field.type === 'section') {
            for (const child of field.children ?? []) {
                names.push(...collectMissingLabelFieldNames(child.fields));
            }
            continue;
        }

        if (field.type !== 'wysiwyg' && String(field.label ?? '').trim().length === 0) {
            names.push(field.name);
        }
    }

    return names;
};

const findRootFieldNameWithMissingLabel = (fields: FormField[] | undefined): string | null => {
    for (const field of fields ?? []) {
        if (field.type === 'section') {
            if ((field.children ?? []).some((child) => collectMissingLabelFieldNames(child.fields).length > 0)) {
                return field.name;
            }
            continue;
        }

        if (field.type !== 'wysiwyg' && String(field.label ?? '').trim().length === 0) {
            return field.name;
        }
    }

    return null;
};

type Snapshot = { fields: FormField[]; values: FormValues };

const isRestricted = (field: FormField) =>
    (RESTRICTED_FIELD_NAMES as readonly string[]).includes(field.name) || field.name.includes(TRAININGPLAN_PREFIX);

const createNewField = (): FormField => ({
    name: uuid(),
    type: 'text',
    label: 'Neues Feld',
    rules: [],
    placeholder: ''
});

export const FormAssistent: React.FC<FormAssistentProps> = ({ cb, form, clubSlug }) => {
    const { t } = useTranslation('form');
    const [values, setValues] = useState<FormValues>({
        name: form?.name ?? '',
        mandatory_user_account: form?.mandatory_user_account === undefined ? true : Boolean(form.mandatory_user_account)
    });
    const [fields, setFields] = useState<FormField[]>(
        form?.fields ? ensureUniqueFieldNames(sanitizeFields(form.fields)) : DEFAULT_FIELDS
    );
    const [activeId, setActiveId] = useState<string | null>(null);
    const [newFieldId, setNewFieldId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({ name: '' });
    const [labelErrorFieldNames, setLabelErrorFieldNames] = useState<string[]>([]);
    const [showTemplatePanel, setShowTemplatePanel] = useState(false);
    const [templateType, setTemplateType] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [rightTab, setRightTab] = useState<'preview' | 'ai'>('preview');

    const [history, setHistory] = useState<Snapshot[]>([]);
    const [future, setFuture] = useState<Snapshot[]>([]);

    const fieldsRef = useRef(fields);
    const valuesRef = useRef(values);
    const dragHistoryCapturedRef = useRef(false);
    useEffect(() => {
        fieldsRef.current = fields;
    }, [fields]);

    useEffect(() => {
        if (labelErrorFieldNames.length === 0) return;

        const next = collectMissingLabelFieldNames(fields);
        if (next.join('|') !== labelErrorFieldNames.join('|')) {
            setLabelErrorFieldNames(next);
        }
    }, [fields, labelErrorFieldNames]);
    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    const pushHistory = useCallback(() => {
        setHistory((prev) => [...prev, { fields: fieldsRef.current, values: valuesRef.current }]);
        setFuture([]);
    }, []);

    const handleUndo = useCallback(() => {
        setHistory((prev) => {
            if (prev.length === 0) return prev;
            const snapshot = prev[prev.length - 1];
            setFuture((f) => [...f, { fields: fieldsRef.current, values: valuesRef.current }]);
            setFields(snapshot.fields);
            setValues(snapshot.values);
            setActiveId(null);
            return prev.slice(0, -1);
        });
    }, []);

    const handleRedo = useCallback(() => {
        setFuture((prev) => {
            if (prev.length === 0) return prev;
            const snapshot = prev[prev.length - 1];
            setHistory((h) => [...h, { fields: fieldsRef.current, values: valuesRef.current }]);
            setFields(snapshot.fields);
            setValues(snapshot.values);
            setActiveId(null);
            return prev.slice(0, -1);
        });
    }, []);

    const { addModal, removeModal } = useModalManager();
    const { mutate } = useSWRConfig();
    const api = useApi();

    const hasTrainingplanFields = fields.some((f) => f.name.includes(TRAININGPLAN_PREFIX));
    const hasInvoiceFields = fields.some((f) => f.name.includes(INVOICE_PREFIX));

    // ── Modal helpers ──────────────────────────────────────────────────────────

    const openModal = useCallback(
        ({ title, content }: { title: string; content: React.ReactNode | ((id: string) => React.ReactNode) }) => {
            addModal({
                title,
                content,
                show: true
            });
        },
        [addModal]
    );

    // ── Submit ─────────────────────────────────────────────────────────────────

    const handleSubmit = useConfirmedCallback(
        () => {
            if (fields.length === 0) return;
            const isEdit = Boolean(form?.id);
            const normalizedFields = sanitizeFields(fields);
            const missingLabelFieldNames = collectMissingLabelFieldNames(normalizedFields);

            if (missingLabelFieldNames.length > 0) {
                setLabelErrorFieldNames(missingLabelFieldNames);
                setActiveId(findRootFieldNameWithMissingLabel(normalizedFields));
                alert(t('builder.alerts.missingLabel'));
                return;
            }

            setLabelErrorFieldNames([]);

            const invalidOptionField = normalizedFields.find((field, index) => {
                if (field.type === 'select' || field.type === 'payment_options') {
                    return !Array.isArray(field.options) || field.options.length === 0;
                }

                if (field.type === 'checkbox' && Array.isArray(fields[index]?.options)) {
                    return (
                        Array.isArray(field.options) && field.options.length === 0 && fields[index].options.length > 0
                    );
                }

                return false;
            });

            if (invalidOptionField) {
                alert(
                    t('builder.alerts.missingOption', {
                        field: invalidOptionField.label || t('builder.alerts.fieldFallback')
                    })
                );
                return;
            }

            setIsLoading(true);

            const payload: FormSubmitPayload = { ...values, fields: normalizedFields };

            api({
                url: isEdit ? `form/${form!.id}` : `club/${clubSlug}/forms`,
                method: isEdit ? 'PUT' : 'POST',
                data: payload,
                onSuccess: (data) => {
                    if (!isEdit) alert(t('builder.alerts.created'));
                    // Revalidate this club's form list plus the edited form's own
                    // cache entry, which the picker, FormMapping and useFetchFields
                    // read separately via `form/{id}`.
                    void mutate(
                        (key) =>
                            typeof key === 'string' &&
                            (key.startsWith(`club/${clubSlug}/forms`) || key === `form/${form?.id}`)
                    );
                    cb(data);
                },
                getValidation: (e) => setErrors(e as Record<string, string>),
                onFinish: () => setIsLoading(false)
            });
        },
        {
            enabled: !hasTrainingplanFields,
            header: t('builder.confirm.missingRequirementsHeader'),
            message: t('builder.confirm.missingRequirementsMessage')
        }
    );

    // ── Field management ───────────────────────────────────────────────────────

    const addNewField = useCallback(() => {
        pushHistory();
        const newField = createNewField();
        setFields((prev) => [...prev, newField]);
        setNewFieldId(newField.name);
        setActiveId(newField.name);
    }, [pushHistory]);

    const insertFieldAt = useCallback(
        (index: number) => {
            pushHistory();
            const newField = createNewField();
            setFields((prev) => {
                const next = [...prev];
                next.splice(index, 0, newField);
                return next;
            });
            setNewFieldId(newField.name);
            setActiveId(newField.name);
        },
        [pushHistory]
    );

    const addTemplateFields = useCallback(
        (incoming: FormField | FormField[], checkRestricted = true) => {
            const toAdd = Array.isArray(incoming) ? incoming : [incoming];
            const hasDuplicate = toAdd.some(
                (f) =>
                    (checkRestricted && (RESTRICTED_FIELD_NAMES as readonly string[]).includes(f.name)) ||
                    fields.some((existing) => existing.name === f.name)
            );
            if (hasDuplicate) {
                alert(t('builder.alerts.invalidName'));
                return;
            }
            pushHistory();
            setFields((prev) => [...prev, ...toAdd]);
            setTemplateType('');
            setShowTemplatePanel(false);
        },
        [fields, pushHistory]
    );

    const removeField = useCallback(
        (field: FormField) => {
            if (field.name.includes(TRAININGPLAN_PREFIX)) {
                if (confirm(t('builder.alerts.removeTrainingplan'))) {
                    pushHistory();
                    setFields((prev) => prev.filter((f) => !f.name.includes(TRAININGPLAN_PREFIX)));
                }
            } else {
                pushHistory();
                setFields((prev) => prev.filter((f) => f.name !== field.name));
            }
            setActiveId(null);
        },
        [pushHistory]
    );

    const moveField = useCallback(
        (from: number, to: number) => {
            if (from === to) return;

            if (!dragHistoryCapturedRef.current) {
                pushHistory();
                dragHistoryCapturedRef.current = true;
            }

            setFields((prev) => {
                if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev;

                const next = [...prev];
                const [movedField] = next.splice(from, 1);
                if (!movedField) return prev;
                next.splice(to, 0, movedField);
                return next;
            });
        },
        [pushHistory]
    );

    const finishFieldMove = useCallback(() => {
        dragHistoryCapturedRef.current = false;
    }, []);

    const duplicateField = useCallback(
        (field: FormField) => {
            pushHistory();
            // Deep copy with fresh names/ids — a shallow copy would leave nested
            // section fields sharing names (and thus values) with the original.
            const dup = cloneField(field);
            setFields((prev) => {
                const idx = prev.findIndex((f) => f.name === field.name);
                const next = [...prev];
                next.splice(idx + 1, 0, dup);
                return next;
            });
            setNewFieldId(dup.name);
            setActiveId(dup.name);
        },
        [pushHistory]
    );

    const changeFieldType = useCallback(
        (field: FormField, newType: string) => {
            pushHistory();
            const base: FormField = {
                name: field.name,
                label: field.type === 'wysiwyg' ? '' : field.label,
                rules: (field.rules ?? []).filter((r) => r === 'required'),
                type: newType
            };
            // Seed type-specific defaults so child components never receive undefined
            let newField = base;
            if (newType === 'table') {
                newField = {
                    ...base,
                    rows: [],
                    columns: [],
                    disabledTimes: [],
                    minTimes: 1,
                    minDays: 1
                };
            } else if (newType === 'select') {
                newField = { ...base, options: ['', ''] };
            } else if (newType === 'payment_options') {
                newField = { ...base, options: [] };
            } else if (newType === 'text' || newType === 'textarea' || newType === 'iban') {
                newField = { ...base, placeholder: '' };
            } else if (newType === 'address') {
                newField = { ...base, helperText: '' };
            } else if (newType === 'checkbox') {
                newField = { ...base, selectionMode: 'multiple' };
            } else if (newType === 'pricing_table') {
                newField = { ...base, selectionMode: 'multiple' };
            } else if (newType === 'section') {
                newField = {
                    ...base,
                    helperText: '',
                    sectionMode: 'default',
                    children: [{ id: uuid(), label: 'Schritt 1', helperText: '', fields: [] }]
                };
            }
            setFields((prev) => prev.map((f) => (f.name === field.name ? newField : f)));
        },
        [pushHistory]
    );

    const updateFieldLabel = useCallback((fieldName: string, label: string) => {
        setFields((prev) => prev.map((f) => (f.name === fieldName ? { ...f, label } : f)));
    }, []);

    // Patches a single key on the current state entry — spreading the `field`
    // argument instead would discard any update batched before this one.
    const updateField = useCallback(<K extends keyof FormField>(field: FormField, value: FormField[K], key: K) => {
        setFields((prev) => prev.map((f) => (f.name === field.name ? { ...f, [key]: value } : f)));
    }, []);

    const replaceField = useCallback((updated: FormField) => {
        setFields((prev) => prev.map((f) => (f.name === updated.name ? updated : f)));
    }, []);

    // ── Template panel options ─────────────────────────────────────────────────

    const renderTemplateOptions = () => {
        switch (templateType) {
            case 'text':
                return <TextFieldOptions cb={addTemplateFields} />;
            case 'date':
                return <DateFieldOptions cb={addTemplateFields} />;
            case 'iban':
                return <IBANFieldOptions cb={addTemplateFields} />;
            case 'table':
                return <TableOptions cb={addTemplateFields} />;
            case 'checkbox':
                return <CheckboxOptions cb={addTemplateFields} />;
            case 'select':
                return <SelectOptions cb={addTemplateFields} />;
            case 'section':
                return <SectionOptions cb={addTemplateFields} />;
            case 'wysiwyg':
                return <TextOptions cb={addTemplateFields} />;
            case 'legal':
                return <LegalOptions cb={addTemplateFields} />;
            case 'trainingsplan':
                return <TrainingplanOptions tpPrefix={TRAININGPLAN_PREFIX} cb={addTemplateFields} />;
            case 'invoices':
                return <UserInvoicesOptions invoicePrefix={INVOICE_PREFIX} cb={addTemplateFields} />;
            case 'payment_options':
                return <PaymentOptionsField cb={addTemplateFields} />;
            case 'number':
                return <NumberFieldOptions cb={addTemplateFields} />;
            default:
                return <FieldTypeInfo hasTrainingplanFields={hasTrainingplanFields} />;
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="tw-grid tw-h-full tw-grid-cols-1 tw-grid-rows-1 tw-gap-2 tw-overflow-hidden xl:tw-grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                <div
                    className="tw-min-h-0 tw-min-w-0 tw-overflow-y-auto tw-pb-6 tw-pr-6"
                    onClick={() => {
                        setActiveId(null);
                        setNewFieldId(null);
                    }}
                >
                    <div className="tw-bg-foreground tw-mb-6 tw-rounded-2xl tw-border tw-border-t-4 tw-border-solid tw-border-success tw-p-4 tw-shadow-sm">
                        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                            <p className="tw-m-0 tw-font-semibold tw-uppercase tw-tracking-wide tw-text-success">
                                {t('builder.heading')}
                            </p>
                            <div className="tw-flex tw-items-center tw-gap-1">
                                <Tooltip tooltip={t('builder.undo')}>
                                    <Button
                                        isDisabled={history.length === 0}
                                        onClick={handleUndo}
                                        flat
                                        className="!tw-min-h-10 !tw-w-10 !tw-rounded-full"
                                    >
                                        <FontAwesomeIcon icon={faUndo} />
                                    </Button>
                                </Tooltip>
                                <Tooltip tooltip={t('builder.redo')}>
                                    <Button
                                        isDisabled={future.length === 0}
                                        onClick={handleRedo}
                                        flat
                                        className="!tw-min-h-10 !tw-w-10 !tw-rounded-full"
                                    >
                                        <FontAwesomeIcon icon={faRedo} />
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                        <StyledInput
                            label={t('builder.formName')}
                            name="name"
                            type="text"
                            value={values.name}
                            helperText={errors.name || undefined}
                            isInvalid={!!errors.name}
                            bordered
                            rounded
                            fullWidth
                            onChange={(e) => setValues((v) => ({ ...v, name: (e.target as HTMLInputElement).value }))}
                        />
                        <div className="tw-mt-4">
                            <StyledCheckbox
                                type="checkbox"
                                id="mandatory_user_account"
                                checked={values.mandatory_user_account}
                                onChange={(e) => setValues((v) => ({ ...v, mandatory_user_account: e }))}
                            >
                                {t('builder.mandatoryAccount')}
                            </StyledCheckbox>
                        </div>
                    </div>

                    {/* ── Question cards ── */}
                    <AnimatePresence mode="popLayout" initial={false}>
                        {fields.map((field, index) => (
                            <motion.div
                                key={field.name}
                                layout="position"
                                className="tw-mb-4"
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{
                                    opacity: 0,
                                    transition: { duration: 0.12, ease: 'easeIn' }
                                }}
                                transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
                            >
                                <QuestionBlock
                                    field={field}
                                    allFields={fields}
                                    index={index}
                                    isActive={activeId === field.name}
                                    isRestricted={isRestricted(field)}
                                    autoFocus={newFieldId === field.name}
                                    labelError={labelErrorFieldNames.includes(field.name)}
                                    labelErrorFieldNames={labelErrorFieldNames}
                                    onActivate={() => {
                                        setActiveId(field.name);
                                        setNewFieldId(null);
                                    }}
                                    onLabelChange={(label) => updateFieldLabel(field.name, label)}
                                    onTypeChange={(type) => changeFieldType(field, type)}
                                    onFieldUpdate={(key, val) => updateField(field, val, key)}
                                    onReplaceField={replaceField}
                                    onDuplicate={() => duplicateField(field)}
                                    onInsertAbove={() => insertFieldAt(index)}
                                    onInsertBelow={() => insertFieldAt(index + 1)}
                                    onDelete={() => removeField(field)}
                                    onMoveItem={moveField}
                                    onMoveEnd={finishFieldMove}
                                    onOpenModal={openModal}
                                    onCloseModal={removeModal}
                                />
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {/* ── Action bar ── */}
                    <div
                        className="tw-bg-foreground tw-my-2 tw-rounded-2xl tw-border tw-border-dashed tw-border-accents-3 tw-p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
                            <div>
                                <p className="tw-m-0 tw-font-medium">{t('builder.addField.title')}</p>
                                <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-accents-6">
                                    {t('builder.addField.hint')}
                                </p>
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <Button flat color="default" onPress={addNewField}>
                                    <FontAwesomeIcon icon={faPlus} className="tw-mr-2" />
                                    {t('builder.addField.button')}
                                </Button>
                                <Button color="light" onPress={() => setShowTemplatePanel((v) => !v)}>
                                    {t('builder.addField.templateButton')}
                                    <FontAwesomeIcon
                                        icon={showTemplatePanel ? faChevronUp : faChevronDown}
                                        className="tw-ml-2 tw-text-xs"
                                    />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* ── Template panel ── */}
                    {showTemplatePanel && (
                        <div
                            className="tw-bg-foreground tw-mb-6 tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-p-4 tw-shadow-sm"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h5 className="tw-mb-4 tw-font-semibold">{t('builder.templatePanel.title')}</h5>
                            <select
                                className="tw-mb-4 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-0 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors focus:tw-border-success focus:tw-outline-none"
                                value={templateType}
                                onChange={(e) => setTemplateType(e.target.value)}
                            >
                                <option value="">{t('builder.templatePanel.selectType')}</option>
                                <optgroup label={t('builder.templatePanel.groupForms')}>
                                    {!hasTrainingplanFields && (
                                        <option value="trainingsplan">
                                            {t('builder.templatePanel.trainingsplan')}
                                        </option>
                                    )}
                                    {!hasInvoiceFields && (
                                        <option value="invoices">{t('builder.templatePanel.invoices')}</option>
                                    )}
                                    <option value="payment_options">{t('builder.templatePanel.paymentOptions')}</option>
                                    <option value="legal">{t('builder.templatePanel.legal')}</option>
                                </optgroup>
                                <optgroup label={t('builder.templatePanel.groupComplex')}>
                                    <option value="section">{t('builder.templatePanel.section')}</option>
                                    <option value="table">{t('builder.templatePanel.table')}</option>
                                    <option value="wysiwyg">{t('builder.templatePanel.wysiwyg')}</option>
                                    <option value="number">{t('builder.templatePanel.number')}</option>
                                </optgroup>
                            </select>

                            {renderTemplateOptions()}
                        </div>
                    )}

                    {/* ── Mobile preview (collapsible, hidden on xl) ── */}
                    <div
                        className="tw-bg-foreground tw-mb-6 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-shadow-sm xl:tw-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-none tw-bg-transparent tw-p-4 tw-text-left tw-transition-colors hover:tw-bg-accents-1"
                            onClick={() => setShowPreview((v) => !v)}
                        >
                            <span className="tw-font-semibold">{t('builder.preview')}</span>
                            <FontAwesomeIcon
                                icon={showPreview ? faChevronUp : faChevronDown}
                                className="tw-text-xs tw-text-accents-5"
                            />
                        </button>
                        {showPreview && (
                            <div className="tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-p-4">
                                <Form onSubmit={() => {}}>
                                    <FormFields fields={fields as any} />
                                </Form>
                            </div>
                        )}
                    </div>

                    <div className="tw-flex tw-justify-end" onClick={(e) => e.stopPropagation()}>
                        <Button color="default" isLoading={isLoading} onPress={handleSubmit}>
                            {t('builder.save')}
                        </Button>
                    </div>
                </div>
                <div className="tw-hidden tw-h-full tw-flex-col xl:tw-flex">
                    <div className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-accents-3 tw-bg-white">
                        <div className="tw-flex tw-shrink-0 tw-border-accents-2">
                            {(['preview', 'ai'] as const).map((tab) => (
                                <Button
                                    disableAnimation
                                    key={tab}
                                    type="button"
                                    color={rightTab === tab ? 'success' : 'light'}
                                    onClick={() => setRightTab(tab)}
                                    className="!tw-rounded-none first:!tw-rounded-tl-2xl last:!tw-rounded-tr-2xl"
                                    block
                                >
                                    {tab === 'preview' ? t('builder.preview') : t('builder.aiAssistant')}
                                </Button>
                            ))}
                        </div>

                        <div
                            className={`tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4 ${
                                rightTab === 'preview' ? '' : 'tw-hidden'
                            }`}
                        >
                            {fields.length === 0 ? (
                                <p className="tw-py-6 tw-text-center tw-text-sm tw-text-accents-5">
                                    {t('builder.noFields')}
                                </p>
                            ) : (
                                <Form onSubmit={() => {}}>
                                    <FormFields fields={fields as any} />
                                </Form>
                            )}
                        </div>

                        <div className={`tw-min-h-0 tw-flex-1 ${rightTab === 'ai' ? '' : 'tw-hidden'}`}>
                            <FormKIAssistant
                                currentForm={{
                                    name: values.name,
                                    mandatory_user_account: values.mandatory_user_account,
                                    fields
                                }}
                                onApply={(generated: GeneratedForm) => {
                                    pushHistory();
                                    setValues({
                                        name: generated.name,
                                        mandatory_user_account: generated.mandatory_user_account
                                    });
                                    setFields(ensureUniqueFieldNames(sanitizeFields(generated.fields)));
                                    setActiveId(null);
                                    setRightTab('preview');
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </DndProvider>
    );
};
